/* eslint-disable no-nested-ternary */
/* eslint-disable import/no-unresolved */
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BgColorsOutlined,
  DollarCircleOutlined,
  ExpandOutlined,
  GiftOutlined,
  MessageOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  Col,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Tooltip,
} from 'antd';
import OfferTypeBadge from 'components/BusinessLogic/Product/OfferTypeBadge';
import Monetary from 'components/UI/Data/Monetary';
import InfoItemWithCopy from 'components/UI/InfoItemWithCopy';
import PopoverCalendar from 'components/UI/PopoverCalendarDates';
import Quality from 'components/UI/Quality';
import Share from 'components/UI/Share';
import { INVALID_QUANTITY_MESSAGE } from 'constants/Global/QuantityValidation.const';
import { PackingTypeEnum } from 'enums/packingType';
import { PackingTypeSale, PackingTypeSaleMap } from 'enums/packingTypeSale';
import Packing from 'model/Packing';
import ProductOffer from 'model/ProductOffer';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ButtonPacking } from 'screens/OfferDetails/components/ButtonPacking';
import { sessionSelectors } from 'store/state/selectors';
import { sessionActions } from 'store/state/slices';
import { calculateMinBySaleType } from 'utils/util';
import { validatorUtils } from 'utils/validator';

import {
  ProductBuy,
  ProductBuyHandle,
} from 'components/BusinessLogic/Product/ProductBuy';
import { Loader, Overlay } from 'components/Modals/Announcement/styles';
import TrolleyLimitModal from 'components/Modals/TrolleyLimitModal';
import { usePurchaseInfoDisplay } from 'hooks/usePurchaseInfoDisplay';
import gfpOfferStore from 'store/state/gfpOffer/gfpOfferStore';
import offerDetailsStore from 'store/state/offerDetails/offerDetailsStore';
import { useShallow } from 'zustand/react/shallow';
import DetailsImageGallery from '../DetailsImageGallery/DetailsImageGallery';
import * as S from './styles';

type Props = {
  productOffer: ProductOffer;
  setModalIsVisibleComposition: (
    productOfferSelected: ProductOffer,
    packingSelected: Packing,
  ) => void;
  setOfferIdModalComposition: React.Dispatch<
    React.SetStateAction<number | undefined>
  >;
  onImageClick?: (images: string[], title: string) => void;
  isExclusiveOffer?: boolean;
};

const { Option } = Select;

export default function ProductOfferDetails({
  productOffer,
  setModalIsVisibleComposition,
  setOfferIdModalComposition,
  onImageClick,
  isExclusiveOffer = false,
}: Props) {
  const {
    offerId,
    offerType,
    voucherId,
    longName,
    conceptName,
    defaultImage,
    images,
    barcode,
    gtincode,
    voucherQuantityType,
    voucherReajustmentType,
    quality,
    packings,
    colors,
    name,
    siteName,
    dimension,
    minimumUnitQuantityVoucher,
    reajustmentAmount,
    layerPrice,
    packagingPrice,
    startDate,
    endDate,
    lkpAuctionDate,
    observation,
    minimumQuantity,
    specialPrice,
    siteDeliveryPatterns,
    isCompositionEnabled,
  } = productOffer;

  const { packingSelected, setPackingSelected } = gfpOfferStore(
    useShallow((state) => ({
      packingSelected: state.packingSelected,
      setPackingSelected: state.setPackingSelected,
    })),
  );

  const {
    hasShippingFee,
    billingDateValue,
    deliveryDateValue,
    setDisplayModalInformation,
    setDisplayModalInformationComposition,
    setHandleOnConfirmationDeliveryDate,
    setHandleOnConfirmationDeliveryDateComposition,
    isLoading,
    setIsLoading,
  } = offerDetailsStore(
    useShallow((state) => ({
      hasShippingFee: state.hasShippingFee,
      billingDateValue: state.billingDateValue,
      deliveryDateValue: state.deliveryDateValue,
      setDisplayModalInformation: state.setDisplayModalInformation,
      setDisplayModalInformationComposition:
        state.setDisplayModalInformationComposition,
      setHandleOnConfirmationDeliveryDate:
        state.setHandleOnConfirmationDeliveryDate,
      setHandleOnConfirmationDeliveryDateComposition:
        state.setHandleOnConfirmationDeliveryDateComposition,
      isLoading: state.isLoading,
      setIsLoading: state.setIsLoading,
    })),
  );

  const [form] = Form.useForm();
  const [fastBuy, setFastBuy] = useState(false);
  const { packingSelectedId, packingTypeSale } = useParams<{
    packingSelectedId: string;
    packingTypeSale: string;
  }>();

  const [initialPackingSelected, setInitialPackingSelected] = useState(
    Number(packingSelectedId),
  );

  const productBuyRef = useRef<ProductBuyHandle>(null);

  const handleSubmit = (formValues: any) => {
    productBuyRef.current?.verifyXpedAndBuyFunc(formValues);
  };

  useEffect(() => {
    if (packings) {
      const packing =
        packings.find((packing) => packing.id === Number(packingSelectedId)) ??
        packings.find(
          (packing) => packing.type === PackingTypeEnum.RETORNAVEL,
        ) ??
        packings.find((packing) => packing.code === '800') ??
        packings[0];

      setPackingSelected(packing);
      setInitialPackingSelected(packing.id);
    }
  }, [
    setPackingSelected,
    packingSelectedId,
    packings,
    form,
    setInitialPackingSelected,
  ]);

  const isPackingDisabled = useMemo(
    () =>
      !productOffer?.packagingPrice ||
      !packingSelected.maxAvailablePackingsQuantity,
    [
      productOffer?.packagingPrice,
      packingSelected.maxAvailablePackingsQuantity,
    ],
  );
  const isLayerDisabled = useMemo(
    () =>
      !productOffer?.layerPrice || !packingSelected.maxAvailableLayersQuantity,
    [productOffer?.layerPrice, packingSelected.maxAvailableLayersQuantity],
  );

  const initialPackingType = useMemo(() => {
    if (!isPackingDisabled) {
      return PackingTypeSale.Packing;
    }

    if (!isLayerDisabled) {
      return PackingTypeSale.Layer;
    }

    return PackingTypeSale.Trolley;
  }, [isPackingDisabled, isLayerDisabled]);

  const [selectedTypeSale, setSelectedTypeSale] = useState<PackingTypeSale>(
    packingTypeSale ? (packingTypeSale as PackingTypeSale) : initialPackingType,
  );

  useEffect(() => {
    const isCurrentDisabled =
      (selectedTypeSale === PackingTypeSale.Packing && isPackingDisabled) ||
      (selectedTypeSale === PackingTypeSale.Layer && isLayerDisabled) ||
      (selectedTypeSale === PackingTypeSale.Trolley &&
        !productOffer?.trolleyPrice);

    if (isCurrentDisabled) {
      setSelectedTypeSale(initialPackingType);
    }
  }, [
    isPackingDisabled,
    isLayerDisabled,
    productOffer?.trolleyPrice,
    initialPackingType,
    selectedTypeSale,
  ]);

  const dispatch = useDispatch();

  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );
  const [textInputTypeSale, setTextInputTypeSale] = useState<string>(() => {
    if (
      packingTypeSale &&
      Object.values(PackingTypeSale).includes(
        packingTypeSale as unknown as PackingTypeSale,
      )
    ) {
      return `${PackingTypeSaleMap[packingTypeSale as PackingTypeSale]}(s)`;
    }
    if (packagingPrice) {
      return 'Embalagem(s)';
    }
    if (layerPrice) {
      return 'Camada(s)';
    }
    return 'Carrinho(s)';
  });

  const [quantityPacking, setQuantityPacking] = useState(0);
  const isQuantityValid = useMemo(() => quantityPacking > 0, [quantityPacking]);
  const [showTrolleyModal, setShowTrolleyModal] = useState(false);
  const [pendingPodAction, setPendingPodAction] = useState<'addToCart' | 'fastBuy'>('addToCart');

  const podShippingFee = useMemo(
    () =>
      hasShippingFee
        ? productOffer.shippingFeeFilials?.[0]?.productShippingValue ?? 0
        : 0,
    [hasShippingFee, productOffer.shippingFeeFilials],
  );

  const purchaseInfo = usePurchaseInfoDisplay(
    selectedTypeSale,
    packingSelected,
    quantityPacking,
    podShippingFee,
  );

  const customerSelected = useSelector(sessionSelectors.getSelectedCustomer);
  const billingDate = useSelector(sessionSelectors.getSelectedBillingDate);
  const deliveryDate = useSelector(sessionSelectors.getSelectedDeliveryDate);
  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);

  const handleTypeSale = (value: PackingTypeSale) => {
    if (value === PackingTypeSale.Packing) {
      setSelectedTypeSale(PackingTypeSale.Packing);
      setTextInputTypeSale(`${PackingTypeSaleMap.Packing}(s)`);
      return;
    }
    if (value === PackingTypeSale.Layer) {
      setSelectedTypeSale(PackingTypeSale.Layer);
      setTextInputTypeSale(`${PackingTypeSaleMap.Layer}(s)`);
      return;
    }
    if (value === PackingTypeSale.Trolley) {
      setSelectedTypeSale(PackingTypeSale.Trolley);
      setTextInputTypeSale(`${PackingTypeSaleMap.Trolley}(s)`);
    }
  };

  const clickCloseModalInformationComposition = useCallback(() => {
    setDisplayModalInformationComposition(false);
  }, [setDisplayModalInformationComposition]);

  const handleOnConfirmationDeliveryDateComposition = useCallback(() => {

    const { billingDateValue: billing, deliveryDateValue: delivery } =
      offerDetailsStore.getState();
    const isLkp = offerType === 1 || offerType === 2;
    const preparedBillingDate = isLkp
      ? lkpAuctionDate.toString()
      : billing?.set('hour', 0).format('YYYY-MM-DD') ?? '';
    const preparedDeliveryDate = isLkp
      ? lkpAuctionDate.toString()
      : delivery?.set('hour', 0).format('YYYY-MM-DD') ?? '';

    if (!preparedBillingDate || !preparedDeliveryDate) {
      return;
    }

    dispatch(
      sessionActions.setDates({
        billingDate: preparedBillingDate,
        deliveryDate: preparedDeliveryDate,
      }),
    );
    clickCloseModalInformationComposition();
    setModalIsVisibleComposition(productOffer, packingSelected);
    setOfferIdModalComposition(offerId);
  }, [
    dispatch,
    lkpAuctionDate,
    offerId,
    offerType,
    packingSelected,
    productOffer,
    setModalIsVisibleComposition,
    setOfferIdModalComposition,
    clickCloseModalInformationComposition,
  ]);

  const handleVisibleModalComposition = useCallback(() => {
    if (deliveryDate && billingDate) {
      setModalIsVisibleComposition(productOffer, packingSelected);
      setOfferIdModalComposition(offerId);
    } else {
      setHandleOnConfirmationDeliveryDateComposition(
        handleOnConfirmationDeliveryDateComposition,
      );
      setDisplayModalInformationComposition(true);
    }
  }, [
    billingDate,
    deliveryDate,
    offerId,
    packingSelected,
    productOffer,
    setModalIsVisibleComposition,
    setOfferIdModalComposition,
    setHandleOnConfirmationDeliveryDateComposition,
    handleOnConfirmationDeliveryDateComposition,
    setDisplayModalInformationComposition,
  ]);

  const clickCloseModalInformation = useCallback(() => {
    setDisplayModalInformation(false);
  }, [setDisplayModalInformation]);

  const getLabel = useCallback(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return packingSelected?.maxAvailablePackingsLabel || '';
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return packingSelected?.maxAvailableLayersLabel || '';
    }
    return packingSelected?.maxAvailableTrolleysLabel || '';
  }, [
    selectedTypeSale,
    packingSelected.maxAvailableTrolleysLabel,
    packingSelected.maxAvailableLayersLabel,
    packingSelected.maxAvailablePackingsLabel,
  ]);

  const getMaxAvailable = useMemo(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return {
        label: packingSelected?.maxAvailablePackingsLabel || '',
        max: packingSelected?.maxAvailablePackingsQuantity || 0,
        min: packingSelected?.unitsPerPackage,
      };
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      return {
        label: packingSelected?.maxAvailableLayersLabel || '',
        max: packingSelected?.maxAvailableLayersQuantity || 0,
        min:
          packingSelected?.packagingByLayer * packingSelected?.unitsPerPackage,
      };
    }
    return {
      label: packingSelected?.maxAvailableTrolleysLabel || '',
      max: packingSelected?.maxAvailableTrolleysQuantity || 0,
      min:
        packingSelected?.packagingByLayer *
        packingSelected?.layersByTrolley *
        packingSelected?.unitsPerPackage,
    };
  }, [packingSelected, selectedTypeSale]);

  useEffect(() => {
    const quantityPackingValue = form.getFieldValue(['quantityPacking']);
    if (quantityPackingValue) {
      form.validateFields(['quantityPacking']);
    }
  }, [getMaxAvailable, form]);

  const handleOnSelect = (id: number) => {
    packings.find((packing) => {
      if (packing.id === id) {
        setPackingSelected(packing);
        return true;
      }
      return false;
    });
  };

  const getMultipleCalc = useCallback(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return (
        packingSelected?.multiple / +packingSelected?.maxAvailablePackingsLabel
      );
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        .split('x')
        .map((x) => +x);
      const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
      return calc < 1 ? 1 : calc;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      .split('x')
      .map((x) => +x);
    const calc = packingSelected?.multiple / values.reduce((a, b) => +a * +b);
    return calc < 1 ? 1 : calc;
  }, [
    selectedTypeSale,
    packingSelected?.multiple,
    packingSelected.maxAvailableTrolleysLabel,
    packingSelected.maxAvailableLayersLabel,
    packingSelected.maxAvailablePackingsLabel,
  ]);

  const handleOnConfirmationDeliveryDate = useCallback(() => {
    dispatch(
      sessionActions.setDates({
        billingDate:
          offerType === 1 || offerType === 2
            ? lkpAuctionDate.toString()
            : billingDateValue?.set('hour', 0).format('YYYY-MM-DD') ?? '',
        deliveryDate:
          offerType === 1 || offerType === 2
            ? lkpAuctionDate.toString()
            : deliveryDateValue?.set('hour', 0).format('YYYY-MM-DD') ?? '',
      }),
    );
    clickCloseModalInformation();
    form.submit();
  }, [
    billingDateValue,
    deliveryDateValue,
    dispatch,
    form,
    lkpAuctionDate,
    offerType,
    clickCloseModalInformation,
  ]);

  const onSetDisplayModalInformation = useCallback(
    (display: boolean) => {
      if (display) {
        setHandleOnConfirmationDeliveryDate(handleOnConfirmationDeliveryDate);
      }

      setDisplayModalInformation(display);
    },
    [
      setHandleOnConfirmationDeliveryDate,
      handleOnConfirmationDeliveryDate,
      setDisplayModalInformation,
    ],
  );

  const getErrorMinMessage = useCallback(() => {
    if (selectedTypeSale === PackingTypeSale.Packing) {
      return `Quantidade mínima: ${
        packingSelected?.minimumQuantity /
        +packingSelected?.maxAvailablePackingsLabel
      } ${textInputTypeSale}`;
    }
    if (selectedTypeSale === PackingTypeSale.Layer) {
      const values = packingSelected?.maxAvailableLayersLabel
        .split('x')
        .map((x) => +x);
      const calc =
        packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
      return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
    }
    const values = packingSelected?.maxAvailableTrolleysLabel
      .split('x')
      .map((x) => +x);
    const calc =
      packingSelected?.minimumQuantity / values.reduce((a, b) => +a * +b);
    return `Quantidade mínima: ${calc < 1 ? 1 : calc} ${textInputTypeSale}`;
  }, [
    selectedTypeSale,
    packingSelected?.maxAvailableTrolleysLabel,
    packingSelected?.minimumQuantity,
    packingSelected?.maxAvailablePackingsLabel,
    packingSelected?.maxAvailableLayersLabel,
    textInputTypeSale,
  ]);

  const infoVoucher = useMemo(
    () =>
      `*Para aplicação do ${
        reajustmentAmount < 0 ? 'desconto' : 'adicional'
      } a quantidade mínima deve ser de ${minimumUnitQuantityVoucher} ${
        voucherQuantityType === 'P'
          ? minimumUnitQuantityVoucher > 1
            ? 'embalagens'
            : 'embalagem'
          : voucherQuantityType === 'L'
          ? minimumUnitQuantityVoucher > 1
            ? 'camadas'
            : 'camada'
          : minimumUnitQuantityVoucher > 1
          ? 'carrinhos'
          : 'carrinho'
      }. O valor ${
        reajustmentAmount < 0 ? 'do desconto' : 'adicional'
      } será aplicado sobre o preço do produto`,
    [reajustmentAmount, minimumUnitQuantityVoucher, voucherQuantityType],
  );

  const handleClickFastBuy = useCallback(() => {
    if (!isQuantityValid) {
      return;
    }
    if (purchaseInfo.exceedsTrolley) {
      setPendingPodAction('fastBuy');
      setShowTrolleyModal(true);
      return;
    }
    setFastBuy(true);
    form.submit();
  }, [form, purchaseInfo.exceedsTrolley, isQuantityValid]);

  const handleTrolleyConfirm = useCallback(() => {
    setShowTrolleyModal(false);
    if (pendingPodAction === 'fastBuy') {
      setFastBuy(true);
    }
    form.submit();
  }, [form, pendingPodAction]);

  return (
    <S.Container form={form} onFinish={handleSubmit}>
      <S.Content gutter={16}>
        <Col md={24} lg={11} style={{ width: '100%', padding: '0px' }}>
          {!!reajustmentAmount && (
            <Badge.Ribbon
              text={
                voucherReajustmentType === 'P'
                  ? `${reajustmentAmount}%`
                  : `R$${reajustmentAmount}`
              }
              color="green"
              style={{ right: '8px' }}
            />
          )}
          <DetailsImageGallery
            defaultImage={defaultImage}
            images={images}
            isLkpOffer={productOffer.isLkpOffer}
            productName={name}
            onImageClick={onImageClick}
          />
        </Col>
        <S.ProductCardComponentDetails
          md={24}
          lg={13}
          style={{ padding: '0px' }}
        >
          <S.OfferInformation>
            <S.OfferInformationTextContainer>
              <S.BadgeContainer>
                <OfferTypeBadge
                  hideCalendar
                  offerType={isExclusiveOffer ? 4 : offerType}
                />
              </S.BadgeContainer>
              {productOffer.isLkpOffer
                ? startDate && (
                    <S.AuctionContainer>
                      <S.AuctionValidity>Oferta válida até:</S.AuctionValidity>
                      <S.AuctionDateContainer>
                        <S.AuctionText>Leilão de</S.AuctionText>
                        <S.AuctionDate>
                          {moment.utc(
                            productOffer.lkpAuctionDate || endDate || startDate,
                          ).format('DD/MM/YYYY')}
                        </S.AuctionDate>
                      </S.AuctionDateContainer>
                    </S.AuctionContainer>
                  )
                : (startDate || endDate) && (
                    <>
                      <S.AuctionContainer>
                        <S.AuctionValidity>
                          Oferta válida até:
                        </S.AuctionValidity>
                        <S.AuctionDateContainer>
                          {startDate && (
                            <S.AuctionDate>
                              {moment(startDate).format('DD/MM/YYYY')}
                            </S.AuctionDate>
                          )}
                          {startDate && endDate && (
                            <S.AuctionText> até </S.AuctionText>
                          )}
                          {endDate && (
                            <S.AuctionDate>
                              {moment(endDate).format('DD/MM/YYYY')}
                            </S.AuctionDate>
                          )}
                        </S.AuctionDateContainer>
                      </S.AuctionContainer>
                      <PopoverCalendar
                        startDate={startDate}
                        endDate={endDate}
                        siteDeliveryPatterns={siteDeliveryPatterns}
                      />
                    </>
                  )}
            </S.OfferInformationTextContainer>
            <div className="info">
              {offerType === 3 && (
                <S.ComponentInfoDirected>
                  Oferta Direcionada
                </S.ComponentInfoDirected>
              )}
            </div>
            <Share label="Compartilhar oferta" />
          </S.OfferInformation>
          <S.Divider />
          <Row className="row-details">
            <S.ProductNameType className="col-details" md={24}>
              <div className="name-product">
                <Tooltip title={longName}>
                  <S.ProductNameEllipsis>{longName}</S.ProductNameEllipsis>
                </Tooltip>
                {conceptName && (
                  <Tooltip title={conceptName}>
                    <S.ConceptNameDetail>{conceptName}</S.ConceptNameDetail>
                  </Tooltip>
                )}
                <S.CodeProductContainer>
                  <InfoItemWithCopy
                    label="Cód. do Produto"
                    value={barcode}
                    fallback="Não informado"
                    inline
                  />
                  <S.CodeProductSeparator>|</S.CodeProductSeparator>
                  <InfoItemWithCopy
                    label="Cód. de Barras"
                    value={gtincode}
                    fallback="Não informado"
                    inline
                  />
                </S.CodeProductContainer>
              </div>
            </S.ProductNameType>
          </Row>
          <Row>
            <Col className="col-information" md={24}>
              <S.InformationProduct>
                <ShopOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Produtor:</p>

                  <Tooltip title={siteName}>
                    <S.ProductNameEllipsis className="producer-name">
                      {siteName}
                    </S.ProductNameEllipsis>
                  </Tooltip>
                </div>
              </S.InformationProduct>
              <S.InformationProduct>
                <TrophyOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Qualidade:</p>
                  <Quality quality={quality} />
                </div>
              </S.InformationProduct>
              <S.InformationProduct>
                <BgColorsOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Cores:</p>
                  <span>{colors}</span>
                </div>
              </S.InformationProduct>
              <S.InformationProduct
                style={{ marginTop: '-6px', marginBottom: '4px' }}
              >
                <GiftOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Embalagem:</p>
                  {packings && packings.length > 0 ? (
                    <Form.Item
                      name="packingSelectedId"
                      initialValue={initialPackingSelected}
                    >
                      <Select
                        style={{ display: 'flex' }}
                        bordered={false}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        onSelect={handleOnSelect}
                      >
                        {packings &&
                          packings.map((packing: Packing) => (
                            <Option
                              value={packing.id}
                              key={`${offerId}-${packing.id}`}
                            >
                              {packing.code} - {packing.abbrev}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  ) : null}
                </div>
              </S.InformationProduct>
              <S.InformationProduct>
                <AppstoreOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Múltiplo:</p>
                  <span>{packingSelected?.multiple}</span>
                </div>
              </S.InformationProduct>
              <S.InformationProduct>
                <ExpandOutlined className="icon-information" />
                <div className="group-information">
                  <p className="title-information">Dimensão/Altura:</p>
                  <span>{dimension}</span>
                </div>
              </S.InformationProduct>
              {observation && (
                <S.InformationProduct>
                  <MessageOutlined className="icon-information" />
                  <div className="group-information">
                    <p className="title-information">Observação:</p>
                    <span>
                      {' '}
                      {observation?.length > 40 ? (
                        <Tooltip title={observation}>
                          {observation?.length > 40
                            ? `${observation.substring(0, 40)}...`
                            : observation}
                        </Tooltip>
                      ) : (
                        observation
                      )}
                    </span>
                  </div>
                </S.InformationProduct>
              )}
              {!!specialPrice && !!minimumQuantity && (
                <S.InformationProduct>
                  <S.InformationVoucher>
                    {minimumQuantity === 1
                      ? `*Para aplicação do valor promocional, a compra mínima deverá ser de 1 unidade`
                      : `*Para aplicação do valor promocional, a compra mínima deverá ser de ${minimumQuantity} unidades`}
                  </S.InformationVoucher>
                </S.InformationProduct>
              )}
            </Col>
          </Row>
          <S.Divider />
          <S.ProductCardComponentActions>
            <Col className="line-actions">
              {isAuthenticated && (
                <>
                  <span className="sale-type-text">
                    Selecione a modalidade de compra e a quantidade:
                  </span>
                  <S.GroupTypeSaleProductContainer>
                    <S.GroupTypeSaleProduct>
                      <ButtonPacking
                        handleTypeSale={handleTypeSale}
                        selectedTypeSale={selectedTypeSale as PackingTypeSale}
                        packingSelected={packingSelected}
                        price={productOffer.packagingPrice}
                        packingCost={productOffer.packingCost}
                        packingType={PackingTypeSale.Packing}
                        quantityPacking={quantityPacking}
                        specialPrice={productOffer.specialPrice}
                        minimumQuantity={productOffer.minimumQuantity}
                      />
                      <div className="divider" />
                      <ButtonPacking
                        handleTypeSale={handleTypeSale}
                        selectedTypeSale={selectedTypeSale as PackingTypeSale}
                        packingSelected={packingSelected}
                        price={productOffer.layerPrice}
                        packingCost={productOffer.packingCost}
                        packingType={PackingTypeSale.Layer}
                        quantityPacking={quantityPacking}
                        specialPrice={productOffer.specialPrice}
                        minimumQuantity={productOffer.minimumQuantity}
                      />
                      <div className="divider" />
                      <ButtonPacking
                        handleTypeSale={handleTypeSale}
                        selectedTypeSale={selectedTypeSale as PackingTypeSale}
                        packingSelected={packingSelected}
                        price={productOffer.trolleyPrice}
                        packingCost={productOffer.packingCost}
                        packingType={PackingTypeSale.Trolley}
                        quantityPacking={quantityPacking}
                        specialPrice={productOffer.specialPrice}
                        minimumQuantity={productOffer.minimumQuantity}
                      />
                    </S.GroupTypeSaleProduct>
                    <S.ShippingFee>
                      Frete:{' '}
                      <Monetary
                        symbol
                        value={
                          productOffer.shippingFeeFilials.length > 0 &&
                          hasShippingFee
                            ? productOffer.shippingFeeFilials[0]
                                ?.productShippingValue
                            : ''
                        }
                      />
                    </S.ShippingFee>
                    <Row className="line-actions-input">
                      <Form.Item
                        name="quantityPacking"
                        rules={[
                          {
                            required: true,
                            message: 'A quantidade é obrigatória.',
                          },
                          {
                            validator: (_, value) =>
                              !value || Number(value) <= getMaxAvailable.max
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(
                                      `Quantidade máxima: ${getMaxAvailable.max} ${textInputTypeSale}`,
                                    ),
                                  ),
                          },
                          {
                            validator: (_, value) =>
                              (value || Number(value)) >=
                              calculateMinBySaleType(
                                selectedTypeSale,
                                packingSelected,
                              )
                                ? Promise.resolve()
                                : Promise.reject(
                                    new Error(getErrorMinMessage()),
                                  ),
                          },
                          {
                            validator: (_, value) =>
                              validatorUtils.validMultiple(
                                value,
                                packingSelected?.multiple,
                                selectedTypeSale,
                                getLabel(),
                                getMultipleCalc(),
                                textInputTypeSale,
                              ),
                          },
                        ]}
                      >
                        <Input
                          autoComplete="off"
                          onKeyPress={(event) => {
                            if (!/[0-9]/.test(event.key)) {
                              event.preventDefault();
                            }
                          }}
                          className="input-quantity"
                          addonAfter={textInputTypeSale}
                          onChange={({ target }) => {
                            const value = Number(target.value);
                            if (value >= 0) {
                              setQuantityPacking(value);
                              form.setFieldsValue({ quantityPacking: value });
                            }
                          }}
                        />
                        <Col className="line-actions-quantity">
                          <S.InfoColumnContainer>
                            <S.InfoFirstRow>
                              <S.MinQuantityProduct>{purchaseInfo.qtdMinimaText}</S.MinQuantityProduct>
                              <span>Total disponível: {getMaxAvailable.max}</span>
                              <S.ShippingFee>{purchaseInfo.shippingFeeText}</S.ShippingFee>
                            </S.InfoFirstRow>
                            {purchaseInfo.compositionLine && (
                              <S.CompositionLine>{purchaseInfo.compositionLine}</S.CompositionLine>
                            )}
                            {purchaseInfo.totalPedidoText && (
                              <S.TotalPedidoLine>{purchaseInfo.totalPedidoText}</S.TotalPedidoLine>
                            )}
                          </S.InfoColumnContainer>
                          <Popover
                            content={
                              <div style={{ maxWidth: '180px' }}>
                                <span>
                                  Utilize produtos diferentes para preencher uma
                                  mesma camada.
                                </span>
                              </div>
                            }
                            title="Criar composição"
                          >
                            {isCompositionEnabled && (
                              <Button
                                type="primary"
                                className="button-composition"
                                onClick={handleVisibleModalComposition}
                              >
                                <ApartmentOutlined />
                              </Button>
                            )}
                          </Popover>
                        </Col>
                      </Form.Item>
                    </Row>
                  </S.GroupTypeSaleProductContainer>
                  {!productOffer.isLkpOffer && voucherId && (
                    <S.VoucherInfoGreen>{infoVoucher}</S.VoucherInfoGreen>
                  )}
                </>
              )}
            </Col>
          </S.ProductCardComponentActions>
          <S.ButtonsContainer>
            {customerSelected && !isProducerAuthenticated && (
              <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
                <S.ButtonTooltipWrapper isDisabled={!isQuantityValid}>
                  <button
                    type="button"
                    className="button-cart"
                    disabled={!isQuantityValid}
                    onClick={() => {
                      if (!isQuantityValid) {
                        return;
                      }
                      if (purchaseInfo.exceedsTrolley) {
                        setPendingPodAction('addToCart');
                        setShowTrolleyModal(true);
                        return;
                      }
                      form.submit();
                    }}
                  >
                    Adicionar ao Carrinho
                    <ShoppingCartOutlined className="icon" />
                  </button>
                </S.ButtonTooltipWrapper>
              </Tooltip>
            )}
            {customerSelected && productOffer.isLkpOffer && (
              <Tooltip title={isQuantityValid ? '' : INVALID_QUANTITY_MESSAGE}>
                <S.ButtonTooltipWrapper isDisabled={!isQuantityValid}>
                  <button
                    type="button"
                    className="buy-now"
                    disabled={!isQuantityValid}
                    onClick={handleClickFastBuy}
                  >
                    Comprar agora
                    <DollarCircleOutlined className="icon" />
                  </button>
                </S.ButtonTooltipWrapper>
              </Tooltip>
            )}
          </S.ButtonsContainer>
        </S.ProductCardComponentDetails>
      </S.Content>
      <ProductBuy
        ref={productBuyRef}
        billingDateValue={billingDateValue}
        deliveryDateValue={deliveryDateValue}
        fastBuy={fastBuy}
        productOffer={productOffer}
        selectedTypeSale={selectedTypeSale}
        onFinish={() => setFastBuy(false)}
        onResetFields={form.resetFields}
        onSetDisplayModalInformation={onSetDisplayModalInformation}
        setLoading={setIsLoading}
      />
      {isLoading && (
        <Overlay>
          <Loader spinning size="large" />
        </Overlay>
      )}

      <TrolleyLimitModal
        visible={showTrolleyModal}
        totalUnits={purchaseInfo.totalUnits}
        quantityOrdered={quantityPacking}
        selectedTypeSale={selectedTypeSale}
        unitsPerType={purchaseInfo.unitsPerType}
        totalPrice={purchaseInfo.totalUnits * (productOffer.packagingPrice || 0)}
        shippingFeeTotal={
          hasShippingFee ? purchaseInfo.totalUnits * podShippingFee : undefined
        }
        onConfirm={handleTrolleyConfirm}
        onCancel={() => setShowTrolleyModal(false)}
      />
    </S.Container>
  );
}
